import { RequestOptions } from 'graphql-request';

import {
  CreateSkuComponentGroupDTO,
  FulfillmentStrategy,
  PatchSkuComponentGroupDTO,
  SkuAssemblyComponentDTO,
  SkuComponentGroupDTO,
  SkuDTO,
  SkuType,
} from '@invenco/products-interface';

import { DEFAULT_PAGE_SIZE } from '@/constants';

import {
  OrderBy,
  SkusBoolExp,
  SkusOrderByExp,
  SkusQueryQuery,
  SkusQueryQueryVariables,
} from '../../graphql/generated/seller/graphql';
import { BaseGateway, QueryRequestOptions } from '../BaseGateway';
import {
  GatewayArrayFilter,
  GatewayBooleanFilter,
  GatewayFilter,
  GatewayName,
  GatewayQueryProps,
  PaginatedResponse,
} from '../types';
import { skusQuery } from './gql/seller/products';

function getFilterWhereClause(
  fieldName: keyof Omit<SkusBoolExp, '_and' | '_not' | '_or'>,
  filter: GatewayFilter,
): SkusBoolExp {
  switch (filter.operator) {
    case 'eq':
      return { [fieldName]: { _eq: filter.value } };
    case 'ne':
      return { [fieldName]: { _neq: filter.value } };
    case 'like':
      return { [fieldName]: { _like: `%${filter.value}%` } };
    case 'notLike':
      return { [fieldName]: { _nlike: `%${filter.value}%` } };
    case 'in':
      return { [fieldName]: { _in: filter.value } };
    case 'notIn':
      return { [fieldName]: { _nin: filter.value } };
    case 'lt':
      return { [fieldName]: { _lt: filter.value } };
    case 'lte':
      return { [fieldName]: { _lte: filter.value } };
    case 'gt':
      return { [fieldName]: { _gt: filter.value } };
    case 'gte':
      return { [fieldName]: { _gte: filter.value } };
    default:
      return {};
  }
}

export type SortField = 'id' | 'name' | 'type' | 'barcode';

export type QuerySkusProps = GatewayQueryProps<{
  sortKey: SortField;
  filters: {
    name: GatewayArrayFilter;
    type: GatewayArrayFilter;
    isArchived: GatewayBooleanFilter;
  };
}>;

export type GetSkusQuery = {
  take?: number;
  cursor?: string;
  search?: string;
  type?: string;
  skuNames?: string[];
  excludeSkuIds?: string[];
  allowOverTake?: boolean;
  archived?: boolean;
};

export class ProductsGateway extends BaseGateway {
  name: GatewayName = 'products';

  async getSkus(
    query?: GetSkusQuery,
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<SkuDTO>> {
    let archived: string | undefined;
    if (typeof query?.archived === 'boolean') {
      archived = query.archived ? 'true' : 'false';
    }
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
      type: query?.type,
      names: query?.skuNames?.join(','),
      excludeSkuIds: query?.excludeSkuIds?.join(','),
      archived,
    };

    const { data } = await this.httpGet('/v1/skus', { params, ...options });
    return { ...data, items: data.skus };
  }

  async getSku(
    productId: string,
    query?: { includes?: 'assemblies' },
    options?: QueryRequestOptions,
  ): Promise<SkuDTO> {
    const { data } = await this.httpGet(`/v1/skus/${productId}`, {
      params: {
        ...query,
      },
      ...options,
    });
    return data.sku;
  }

  async getMany(
    query?: GetSkusQuery,
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<SkuDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
      type: query?.type,
      excludeSkuIds: query?.excludeSkuIds?.join(','),
    };
    const { data } = await this.httpPost('/v1/skus/get_many', {
      data: params,
      ...options,
    });
    return { ...data, items: data.skus };
  }

  async createSku(sku: Partial<SkuDTO>): Promise<SkuDTO> {
    const { data } = await this.httpPost<{ sku: SkuDTO }>('/v1/skus', {
      data: { sku },
    });
    return data.sku;
  }

  async updateSku(id: string, sku: Partial<SkuDTO>): Promise<SkuDTO> {
    const { data } = await this.httpPatch<{ sku: SkuDTO }>(`/v1/skus/${id}`, {
      data: { sku },
    });
    return data.sku;
  }

  async importSku(sku: Partial<SkuDTO>): Promise<SkuDTO> {
    const { data } = await this.httpPost<{ sku: SkuDTO }>('/v1/skus/import', {
      data: { sku },
    });
    return data.sku;
  }

  async convertSku(
    id: string,
    updates: { type: SkuType; fulfillmentStrategy: FulfillmentStrategy },
  ): Promise<SkuDTO> {
    const { data } = await this.httpPost<{ sku: SkuDTO }>(`/v1/skus/${id}/convert`, {
      data: updates,
    });
    return data.sku;
  }

  async updateAssemblyComponents(
    id: string,
    assemblyComponents: Partial<SkuAssemblyComponentDTO>[],
  ): Promise<SkuDTO> {
    const { data } = await this.httpPut<{ sku: SkuDTO }>(`/v1/skus/${id}/assembly_components`, {
      data: { components: assemblyComponents },
    });
    return data.sku;
  }

  async createSkuComponentGroup(
    skuId: string,
    componentGroup: CreateSkuComponentGroupDTO,
  ): Promise<SkuComponentGroupDTO> {
    const { data } = await this.httpPost<{
      componentGroup: SkuComponentGroupDTO;
    }>(`/v1/skus/${skuId}/component_groups`, {
      data: { componentGroup },
    });

    return data.componentGroup;
  }

  async updateSkuComponentGroup(
    skuId: string,
    componentGroupId: string,
    groupData: PatchSkuComponentGroupDTO,
  ): Promise<SkuComponentGroupDTO> {
    const { data } = await this.httpPatch<{
      componentGroup: SkuComponentGroupDTO;
    }>(`/v1/skus/${skuId}/component_groups/${componentGroupId}`, {
      data: { componentGroup: groupData },
    });
    return data.componentGroup;
  }

  async deleteSkuComponentGroup(skuId: string, componentGroupId: string): Promise<void> {
    await this.httpDelete<{ sku: SkuComponentGroupDTO }>(
      `/v1/skus/${skuId}/component_groups/${componentGroupId}`,
    );
  }

  async exportAllSkusToBlob(): Promise<Blob> {
    const { data } = await this.httpGet('/v1/skus/export', {
      responseType: 'blob',
    });
    return data;
  }

  async archiveSku(skuId: string): Promise<SkuDTO> {
    const { data } = await this.httpPost<{ sku: SkuDTO }>(`/v1/skus/${skuId}/archive`);
    return data.sku;
  }

  async unarchiveSku(skuId: string): Promise<SkuDTO> {
    const { data } = await this.httpPost<{ sku: SkuDTO }>(`/v1/skus/${skuId}/unarchive`);
    return data.sku;
  }

  async querySkus(
    input: QuerySkusProps,
    options: { signal?: RequestOptions['signal'] },
  ): Promise<SkusQueryQuery> {
    // Ensure limit is always a positive number
    const limit = Math.max(1, Math.abs(input.take ?? DEFAULT_PAGE_SIZE));

    const whereClause: SkusBoolExp[] = [];

    if (input.search) {
      const searchPattern = `%${input.search}%`;
      whereClause.push({
        _or: [
          { name: { _ilike: searchPattern } },
          { description: { _ilike: searchPattern } },
          { barcode: { _ilike: searchPattern } },
        ],
      });
    }

    const { filters } = input;

    if (filters?.name && filters.name.value.length > 0) {
      whereClause.push(getFilterWhereClause('name', filters.name));
    }
    if (filters?.type && filters.type.value.length > 0) {
      whereClause.push(getFilterWhereClause('type', filters.type));
    }
    if (filters?.isArchived) {
      whereClause.push({
        archivedAt: { _is_null: !filters.isArchived.value },
      });
    }

    /**
     * Sorting
     */
    const orderBy = input.sortBy
      ? input.sortBy.map(({ key, direction }) => {
          const orderByDirection = direction === 'desc' ? OrderBy.Desc : OrderBy.Asc;
          const orderByClause: SkusOrderByExp = {};
          orderByClause[key] = orderByDirection;
          return orderByClause;
        })
      : [];

    const variables: SkusQueryQueryVariables = {
      limit,
      offset: input.offset ?? 0,
      where: whereClause.length > 0 ? { _and: whereClause } : {},
      orderBy,
    };

    return this.graphqlQuery<SkusQueryQuery, SkusQueryQueryVariables>(
      skusQuery,
      variables,
      options.signal,
    );
  }
}
